import { useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { TemplateProvider, useTemplate } from '@/contexts/TemplateContext'
import { BookingWizard } from './BookingWizard'
import type { Service, ServiceCategory, PublicProfile } from '@/types'

interface BookingModalProps {
  isOpen: boolean
  onClose: () => void
  profile: PublicProfile
  services: Service[]
  categories: ServiceCategory[]
}

export const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  profile,
  services,
  categories,
}) => {
  // Handle Escape key press
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  return (
    <TemplateProvider colorScheme={profile.color_scheme} typography={profile.typography}>
      <ModalContent
        onClose={onClose}
        profile={profile}
        services={services}
        categories={categories}
      />
    </TemplateProvider>
  )
}

interface ModalContentProps {
  onClose: () => void
  profile: PublicProfile
  services: Service[]
  categories: ServiceCategory[]
}

const ModalContent: React.FC<ModalContentProps> = ({
  onClose,
  profile,
  services,
  categories,
}) => {
  const { colors } = useTemplate()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl max-h-[90vh] mx-4 rounded-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: colors.colors.background }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-modal-title"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: colors.colors.secondary }}
        >
          <h2
            id="booking-modal-title"
            className="text-xl font-bold"
            style={{ color: colors.colors.text }}
          >
            Book an Appointment
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-colors hover:bg-gray-100"
            style={{ color: colors.colors.textMuted }}
            aria-label="Close booking modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Wizard */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          <BookingWizard
            profileId={profile.id}
            username={profile.slug}
            profileTimezone={profile.timezone}
            services={services}
            categories={categories}
            colorScheme={profile.color_scheme}
            typography={profile.typography}
            defaultCurrency={profile.currency || 'EUR'}
            supportedCurrencies={profile.supported_currencies}
            verificationMethod={profile.booking_settings?.client_verification_method}
            clientVerificationEnabled={profile.booking_settings?.client_verification_enabled ?? true}
            allowCustomerStaffSelection={
              profile.booking_settings?.allow_customer_staff_selection ?? true
            }
            variant="modal"
            onComplete={() => {
              setTimeout(onClose, 2000)
            }}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  )
}